import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Alert,
  GlobalStyles,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import LoopIcon from '@mui/icons-material/Loop'
import { FileUploadState } from '../proposal-context'

const formatErrorMessage = (errorMessage: string): React.ReactNode => {
  if (errorMessage.includes('\n')) {
    const lines = errorMessage.split('\n').filter((line) => line.trim() !== '')
    if (lines.length > 1) {
      return (
        <>
          {lines.map((line, index) => (
            <Box key={index} sx={{ mt: index > 0 ? 0.5 : 0 }}>
              {line.trim().startsWith('•') ? line.trim() : `• ${line.trim()}`}
            </Box>
          ))}
        </>
      )
    }
  }
  return errorMessage
}

type FileStatus = {
  success?: boolean
  error?: string
}

interface FileItemProps {
  fileName: string
  status: FileStatus
}

const FileItem: React.FC<FileItemProps> = ({ fileName, status }) => {
  return (
    <Box
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: status.success === false ? 'error.light' : 'success.light',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {status.success === true ? (
          <Box
            sx={{
              color: 'common.white',
              bgcolor: 'success.main',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckIcon fontSize="small" />
          </Box>
        ) : status.success === false ? (
          <Box
            sx={{
              color: 'common.white',
              bgcolor: 'error.main',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloseIcon fontSize="small" />
          </Box>
        ) : (
          <Box
            sx={{
              color: 'common.white',
              bgcolor: 'info.main',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LoopIcon fontSize="small" />
          </Box>
        )}

        <Typography variant="body1" noWrap sx={{ flex: 1 }}>
          {fileName}
        </Typography>

        <Typography
          variant="body2"
          color={
            status.success === false
              ? 'error.main'
              : status.success === true
                ? 'success.main'
                : 'text.secondary'
          }
          fontWeight="medium"
        >
          {status.success === true
            ? 'Uploaded'
            : status.success === false
              ? 'Failed'
              : 'Processing...'}
        </Typography>
      </Stack>

      {status.error && (
        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
          <Typography variant="body2">
            {formatErrorMessage(status.error)}
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

interface UploadDialogProps {
  open: boolean
  uploadState: FileUploadState
  uploadError: string | null
  creationError: string | null
  isCreatingProposal: boolean
  createdProposalId: string | null
  onCancelUploads: () => Promise<void>
  onRetryFailedUploads: () => Promise<boolean>
  onCreateProposal: () => Promise<string>
  onViewProposal: (proposalId: string) => void
}

const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  uploadState,
  uploadError,
  creationError,
  isCreatingProposal,
  createdProposalId,
  onCancelUploads,
  onRetryFailedUploads,
  onCreateProposal,
  onViewProposal,
}) => {
  return (
    <>
      <GlobalStyles
        styles={{
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>
          {isCreatingProposal ? 'Creating Proposal' : 'Uploading Files'}
        </DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {creationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {creationError}
            </Alert>
          )}
          {isCreatingProposal && (
            <Typography variant="body1" gutterBottom>
              Creating proposal... Please wait.
            </Typography>
          )}

          {!uploadState.uploading &&
            !isCreatingProposal &&
            createdProposalId && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Proposal created successfully!
              </Alert>
            )}

          {Object.entries(uploadState.uploads).map(([fileName, status]) => (
            <FileItem key={fileName} fileName={fileName} status={status} />
          ))}
        </DialogContent>
        <DialogActions>
          {!createdProposalId && (
            <Button
              onClick={() => void onCancelUploads()}
              color="error"
              disabled={uploadState.uploading || isCreatingProposal}
            >
              Cancel
            </Button>
          )}

          {uploadState.hasErrors && !createdProposalId ? (
            <Button
              onClick={() => void onRetryFailedUploads()}
              color="primary"
              disabled={uploadState.uploading || isCreatingProposal}
            >
              Retry Upload
            </Button>
          ) : !uploadState.hasErrors && !createdProposalId ? (
            <Button
              onClick={() => void onCreateProposal()}
              color="primary"
              variant="contained"
              disabled={uploadState.uploading || isCreatingProposal}
            >
              Create Proposal
            </Button>
          ) : createdProposalId ? (
            <Button
              onClick={() => onViewProposal(createdProposalId)}
              color="primary"
              variant="contained"
            >
              View Proposal
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UploadDialog
